import { Component, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { JobFormFuseComponent } from '../booking-lilate/job-form-fuse/job-form-fuse.component';
import { JobService } from '../../service/job.service';
import { BusinessService } from '../../service/business.service';
import { UserService } from '../../service/user.service';

/**
 * Statut option avec icône pour l'onboarding AI
 */
export interface StatutOption {
  value: string;
  labelKey: string;
  emoji: string;
}

/**
 * Composant job-form dédié à l'onboarding AI LILATE (step-3)
 *
 * Parcours en 2 écrans :
 *   - Écran 1 : Sélection du statut (4 boutons 2×2) + Bloc contexte conditionnel
 *               avec les champs métier/secteur (autocomplete hérités)
 *   - Écran 2 : Nom d'entreprise + site internet (requis)
 *
 * Héritage :
 * - JobFormComponent (base) : logique métier, form, validations, API calls
 * - JobFormFuseComponent : override onJobSuggestOptionSelection / onBusinessSuggestOptionSelection
 * - JobFormAiOnboardingFuseComponent : 2 écrans onboarding AI avec statuts enrichis
 */
@Component({
  selector: 'app-job-form-ai-onboarding-fuse',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatRadioModule,
    MatIconModule,
    MatDividerModule,
    TranslateModule,
  ],
  providers: [
    JobService,
    BusinessService,
    UserService,
  ],
  templateUrl: './job-form-ai-onboarding-fuse.component.html',
  styleUrls: ['./job-form-ai-onboarding-fuse.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFormAiOnboardingFuseComponent extends JobFormFuseComponent {

  // ── Écran courant ──
  currentScreen = signal<1 | 2>(1);

  // ── Statut sélectionné ──
  selectedStatut = signal<string | null>(null);

  // ── Champs entreprise (écran 2, requis) ──
  companyNameControl = new FormControl('', [Validators.required]);
  companyWebsiteControl = new FormControl('', [Validators.required, urlValidator()]);

  ngOnInit(): void {
    super.ngOnInit();

    // Pré-remplir le statut depuis les données utilisateur existantes
    if (this.user && this.user.job_situation) {
      this.selectedStatut.set(this.user.job_situation);
    }
  }

  /**
   * 4 statuts avec emojis (grille 2×2, fidèle au HTML de référence)
   * Mapping vers les valeurs backend existantes (employed, unemployed, student, retired)
   */
  statutOptions: StatutOption[] = [
    { value: 'employed',   labelKey: 'examination-ai.onboarding.job.statut.employed',   emoji: '🏢' },
    { value: 'unemployed', labelKey: 'examination-ai.onboarding.job.statut.unemployed', emoji: '🔍' },
    { value: 'student',    labelKey: 'examination-ai.onboarding.job.statut.student',    emoji: '🎓' },
    { value: 'retired',    labelKey: 'examination-ai.onboarding.job.statut.retired',    emoji: '🌴' },
  ];

  /**
   * Statuts nécessitant les champs métier+secteur (sans choix de contexte)
   */
  private statutsTwoFieldsOnly = ['employed', 'retired'];

  // ── Titre/sous-titre adaptatifs du bloc contexte (écran 1) ──
  contexteTitle = computed(() => {
    const statut = this.selectedStatut();
    if (!statut) return '';
    const map: Record<string, string> = {
      student:    'examination-ai.onboarding.job.bloc2.student.title',
      unemployed: 'examination-ai.onboarding.job.bloc2.unemployed.title',
      employed:   'examination-ai.onboarding.job.bloc2.employed.title',
      retired:    'examination-ai.onboarding.job.bloc2.retired.title',
    };
    return map[statut] || '';
  });

  contexteSubtitle = computed(() => {
    const statut = this.selectedStatut();
    if (!statut) return '';
    const map: Record<string, string> = {
      student:    'examination-ai.onboarding.job.bloc2.student.subtitle',
      unemployed: 'examination-ai.onboarding.job.bloc2.unemployed.subtitle',
      employed:   'examination-ai.onboarding.job.bloc2.employed.subtitle',
      retired:    'examination-ai.onboarding.job.bloc2.retired.subtitle',
    };
    return map[statut] || '';
  });

  // ── Sous-titre et hint adaptatifs de l'écran 2 (entreprise) ──
  screen2Subtitle = computed(() => {
    const statut = this.selectedStatut();
    if (!statut) return '';
    const map: Record<string, string> = {
      employed:   'examination-ai.onboarding.job.screen2.employed.subtitle',
      retired:    'examination-ai.onboarding.job.screen2.retired.subtitle',
      student:    'examination-ai.onboarding.job.screen2.student.subtitle',
      unemployed: 'examination-ai.onboarding.job.screen2.unemployed.subtitle',
    };
    return map[statut] || '';
  });

  screen2Hint = computed(() => {
    const statut = this.selectedStatut();
    if (!statut) return '';
    const map: Record<string, string> = {
      employed:   'examination-ai.onboarding.job.screen2.employed.hint',
      retired:    'examination-ai.onboarding.job.screen2.retired.hint',
      student:    'examination-ai.onboarding.job.screen2.student.hint',
      unemployed: 'examination-ai.onboarding.job.screen2.unemployed.hint',
    };
    return map[statut] || '';
  });

  // ── Labels dynamiques pour les champs métier/secteur ──
  jobLabelKey = computed(() => {
    return this.selectedStatut() === 'retired'
      ? 'examination-ai.onboarding.job.field.job.retired'
      : 'examination-ai.onboarding.job.field.job.default';
  });

  sectorLabelKey = computed(() => {
    return this.selectedStatut() === 'retired'
      ? 'examination-ai.onboarding.job.field.sector.retired'
      : 'examination-ai.onboarding.job.field.sector.default';
  });

  /**
   * Indique si le statut sélectionné nécessite toujours les deux champs
   * (employed, retired → pas de choix "secteur seul" / "généraliste" à l'écran 1)
   */
  isTwoFieldsOnly = computed(() => {
    return this.statutsTwoFieldsOnly.includes(this.selectedStatut() || '');
  });

  /**
   * Indique si le bloc 2 (contexte/champs) doit être visible sur l'écran 1
   */
  showBloc2 = computed(() => !!this.selectedStatut());

  /**
   * Indique si les champs métier/secteur doivent être affichés (écran 1)
   * Pour employed/retired : toujours. Pour student/unemployed : après sélection statut.
   */
  showJobFields = computed(() => {
    const statut = this.selectedStatut();
    if (!statut) return false;
    // Pour employed et retired, afficher directement les champs
    if (this.statutsTwoFieldsOnly.includes(statut)) return true;
    // Pour student et unemployed, aussi afficher les champs
    return true;
  });

  /**
   * Sélection d'un statut → met à jour le form hérité + réinitialise
   */
  selectStatut(value: string): void {
    this.selectedStatut.set(value);
    this.businessForm.controls.job_situation.setValue(value);
    this.cdr.detectChanges();
  }

  /**
   * Navigation vers l'écran 2
   */
  goToScreen2(): void {
    this.currentScreen.set(2);
  }

  /**
   * Retour à l'écran 1
   */
  goToScreen1(): void {
    this.currentScreen.set(1);
  }

  /**
   * Texte affiché dans un champ autocomplete (valeur saisie ou document ES)
   */
  private inputText(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    return ((value as { name?: string }).name || '').trim();
  }

  jobInputText(): string {
    return this.inputText(this.searchJobControl.value);
  }

  businessInputText(): string {
    return this.inputText(this.searchBusinessControl.value);
  }

  /**
   * Le métier est validé si le candidat a choisi une entrée de la liste — et que
   * le texte affiché correspond toujours à ce choix — ou s'il a confirmé son
   * libellé via l'option de suggestion.
   */
  isJobResolved(): boolean {
    const suggested = (this.suggestedJobControl.value || '').trim();
    if (suggested.length >= 2) return true;
    if (!this.selectedJob) return false;
    return this.inputText(this.selectedJob.name).toLowerCase() === this.jobInputText().toLowerCase();
  }

  isBusinessResolved(): boolean {
    const suggested = (this.suggestedBusinessControl.value || '').trim();
    if (suggested.length >= 2) return true;
    if (!this.selectedBusiness) return false;
    return this.inputText(this.selectedBusiness.name).toLowerCase() === this.businessInputText().toLowerCase();
  }

  /**
   * Saisie libre non validée : le texte tapé n'est rattaché ni à une entrée de
   * la liste ni à un libellé suggéré, il ne serait donc pas enregistré.
   */
  hasPendingJobInput(): boolean {
    return this.jobInputText().length > 0 && !this.isJobResolved();
  }

  hasPendingBusinessInput(): boolean {
    return this.businessInputText().length > 0 && !this.isBusinessResolved();
  }

  /**
   * Vérifie si l'écran 1 est complet (statut + métier + secteur validés)
   * Métier et secteur sont obligatoires pour tous les statuts.
   */
  canSubmitScreen1(): boolean {
    const statut = this.selectedStatut();
    if (!statut) return false;

    // Une saisie non validée bloque l'écran :
    // sans sélection ni libellé suggéré, le texte saisi serait perdu.
    if (this.hasPendingJobInput() || this.hasPendingBusinessInput()) return false;

    return this.isJobResolved() && this.isBusinessResolved();
  }

  /**
   * Vérifie si le parcours complet est validable (les 2 écrans)
   */
  canSubmitAll(): boolean {
    if (!this.canSubmitScreen1()) return false;

    // Les champs entreprise doivent être remplis et valides
    return this.companyNameControl.valid && this.companyWebsiteControl.valid;
  }

  /**
   * Frappe clavier dans le champ métier : la saisie redevient à valider, le
   * libellé confirmé et l'entrée choisie précédemment ne sont plus valables.
   * On n'écoute pas valueChanges, qui est aussi émis par l'autocomplete lui-même.
   */
  onJobTextInput(): void {
    this.selectedJob = null;
    this.clearJobSuggestion();
  }

  onBusinessTextInput(): void {
    this.selectedBusiness = null;
    this.clearBusinessSuggestion();
  }

  /**
   * Contrôle du champ métier à la sortie du focus : signale une saisie non
   * validée ou un champ obligatoire laissé vide.
   */
  onJobInputBlur(): void {
    this.applyFieldErrors(this.searchJobControl, this.jobInputText(), this.isJobResolved());
  }

  /**
   * Contrôle du champ secteur à la sortie du focus
   */
  onBusinessInputBlur(): void {
    this.applyFieldErrors(this.searchBusinessControl, this.businessInputText(), this.isBusinessResolved());
  }

  private applyFieldErrors(control: FormControl, text: string, resolved: boolean): void {
    let errors: ValidationErrors | null = null;
    if (text.length > 0 && !resolved) {
      errors = { notSelected: true };
    } else if (!text.length) {
      // Métier et secteur obligatoires pour tous les statuts
      errors = { required: true };
    }

    control.setErrors(errors);
    control.markAsTouched();
    this.cdr.detectChanges();
  }

  /**
   * Retourne le nom d'entreprise saisi (pour le patch Lilate dans step-3-job)
   */
  getCompanyName(): string | null {
    return this.companyNameControl.value?.trim() || null;
  }

  /**
   * Retourne le site web saisi, préfixé de https:// si nécessaire
   * (Django URLField exige un protocole complet)
   */
  getCompanyWebsite(): string | null {
    const value = this.companyWebsiteControl.value?.trim() || null;
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    return 'https://' + value;
  }

}

/**
 * Validateur d'URL custom (même logique que lilate-partenaire)
 * Accepte les URLs avec ou sans protocole, vérifie la présence d'un TLD.
 */
function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = control.value.trim();
    if (!value) {
      return null;
    }

    try {
      const url = new URL(value);
      if (!url.hostname.includes('.')) {
        return { invalidUrl: true };
      }
      return null;
    } catch {
      try {
        const url = new URL('https://' + value);
        if (!url.hostname.includes('.')) {
          return { invalidUrl: true };
        }
        return null;
      } catch {
        return { invalidUrl: true };
      }
    }
  };
}
